"use client";

import React from "react";
import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import DataTable from "@/components/ui/DataTable";
import SearchableSelect from "@/components/ui/SearchableSelect";
import DatePicker from "@/components/form/date-picker";
import DrawerPanel from "@/components/ui/DrawerPanel";
import Button from "@/components/ui/button/Button";
import Radio from "@/components/form/input/Radio";
import Checkbox from "@/components/form/input/Checkbox";
import Pagination from "@/components/tables/Pagination";
import { Modal } from "@/components/ui/modal/index";
import InvoicePrint from "@/components/print/InvoicePrint";
import { TrashBinIcon } from "@/icons";
import { extractApiEnvelopeMessage, handleApiError } from "@/lib/api";
import { rmcClient } from "@/lib/rmcClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import toast from "react-hot-toast";

function fmt(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** Detail payloads often use `challans[]` instead of singular `challan`. */
function invoiceChallanDisplay(inv) {
  if (!inv) return { challanNo: "—", batchNo: "—", challanId: "—" };
  const list = Array.isArray(inv.challans) ? inv.challans : [];
  const single = list.length === 1 ? list[0] : null;
  const merged = single ? { ...single, ...(inv.challan || {}) } : inv.challan || null;

  let challanNo = inv.challan_no || merged?.challan_no || "";
  if (!challanNo && list.length === 1) challanNo = list[0]?.challan_no || "";
  if (!challanNo && list.length > 1) {
    const nos = list.map((c) => c.challan_no).filter(Boolean);
    challanNo = nos.length ? nos.join(", ") : `${list.length} challans`;
  }
  if (!challanNo) challanNo = "—";

  const batchNo = merged?.batch_no ?? (list.length === 1 ? list[0]?.batch_no : null) ?? "—";

  let challanId = inv.challan_id ?? merged?.id;
  if (challanId == null && list.length === 1) challanId = list[0]?.id;
  const challanIdStr =
    challanId != null
      ? String(challanId)
      : list.length > 1
        ? `(${list.length})`
        : "—";

  return { challanNo, batchNo: batchNo || "—", challanId: challanIdStr };
}

/** Challan list may expose `is_invoiced`; treat common truthy shapes from APIs. */
function challanIsInvoicedFromPayload(c) {
  if (!c || !("is_invoiced" in c)) return false;
  const v = c.is_invoiced;
  if (v === true || v === 1) return true;
  if (v === false || v === 0 || v == null) return false;
  if (typeof v === "string") return ["true", "1", "yes"].includes(v.trim().toLowerCase());
  return Boolean(v);
}

/** DRF-style list payload; supports `meta` (count, next, previous, total_pages) and top-level fields. */
function readPaginatedListMeta(raw) {
  if (!raw || typeof raw !== "object") {
    return { count: null, hasNext: false, hasPrevious: false, totalPages: null };
  }
  const meta = raw.meta && typeof raw.meta === "object" ? raw.meta : null;
  const tryCount = (obj) => {
    if (!obj || typeof obj !== "object") return null;
    for (const key of ["count", "total", "total_count"]) {
      const n = Number(obj[key]);
      if (Number.isFinite(n) && n >= 0) return n;
    }
    return null;
  };
  const count = tryCount(raw) ?? tryCount(raw.data) ?? tryCount(meta);
  const totalPagesNum = Number(meta?.total_pages);
  const totalPages =
    Number.isFinite(totalPagesNum) && totalPagesNum > 0 ? Math.floor(totalPagesNum) : null;
  return {
    count,
    totalPages,
    hasNext: Boolean(raw.next ?? meta?.next),
    hasPrevious: Boolean(raw.previous ?? meta?.previous),
  };
}

function salesInvoiceSiteLabel(row) {
  if (!row) return "—";
  const direct =
    row.site_name ??
    row.site?.name ??
    row.delivery_site_name ??
    row.challan?.site_name ??
    row.challan?.site?.name;
  if (direct != null && String(direct).trim() !== "") return String(direct);
  const fromChallan = (row.challans || [])
    .map((ch) => ch?.site_name ?? ch?.site?.name)
    .find((v) => v != null && String(v).trim() !== "");
  return fromChallan ? String(fromChallan) : "—";
}

/** Delivery sites from sales parties for invoice filters (searchable dropdown). */
function buildSalesSiteOptions(parties, partyFilterId) {
  const rows = [];
  const seen = new Set();
  for (const p of parties || []) {
    if (
      partyFilterId !== undefined &&
      partyFilterId !== null &&
      partyFilterId !== "" &&
      String(p.id) !== String(partyFilterId)
    ) {
      continue;
    }
    for (const s of p.sites || []) {
      const name = (s.name || s.site_name || "").trim();
      if (!name) continue;
      const id = s.id != null && s.id !== "" ? String(s.id) : `p${p.id}::${name}`;
      if (seen.has(id)) continue;
      seen.add(id);
      rows.push({
        id,
        name,
        siteName: name,
        partyName: (p.name || "").trim(),
      });
    }
  }
  rows.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  return [{ id: "", name: "All sites", siteName: "", partyName: "" }, ...rows];
}

function buildSiteExportParams(siteId, siteOptions) {
  if (!siteId) return {};
  const n = Number(siteId);
  if (Number.isFinite(n) && n > 0 && String(n) === String(siteId).trim()) {
    return { site_id: n };
  }
  const o = siteOptions.find((x) => String(x.id) === String(siteId));
  const name = o?.siteName?.trim();
  return name ? { site_name: name } : {};
}

function challanSiteLabel(challan, parties = []) {
  if (!challan) return "—";
  const direct =
    challan.site_name ??
    (typeof challan.site === "string" ? challan.site : null) ??
    challan.site?.name ??
    challan.delivery_site_name;
  if (direct != null && String(direct).trim() !== "") return String(direct).trim();
  const siteId = challan.site_id ?? challan.site?.id;
  if (siteId == null || siteId === "") return "—";
  const partyId = challan.party_id ?? challan.party?.id;
  const partyList =
    partyId != null && partyId !== ""
      ? parties.filter((p) => String(p.id) === String(partyId))
      : parties;
  for (const p of partyList) {
    const match = (p.sites || []).find((s) => String(s.id) === String(siteId));
    const name = (match?.name || match?.site_name || "").trim();
    if (name) return name;
  }
  return "—";
}

const PAGE_SIZE = 20;

export default function RmcSalesInvoices() {
  const qc = useQueryClient();
  const printRef = React.useRef(null);
  const rowPrintRef = React.useRef(null);
  const isoToday = React.useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, []);
  const [filters, setFilters] = React.useState({
    date_from: isoToday,
    date_to: isoToday,
    party_id: "",
    tax_type: "",
    site_id: "",
  });
  const [page, setPage] = React.useState(1);
  const [isExporting, setIsExporting] = React.useState(false);
  const [detailId, setDetailId] = React.useState(null);
  const [rowPrintInvoice, setRowPrintInvoice] = React.useState(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [createPartyId, setCreatePartyId] = React.useState(null);
  const [createSiteId, setCreateSiteId] = React.useState("");
  const [selectedChallanIds, setSelectedChallanIds] = React.useState([]);
  const [taxType, setTaxType] = React.useState("GST");
  const [invoiceDeleteId, setInvoiceDeleteId] = React.useState(null);

  const { data: parties = [] } = useQuery({
    queryKey: ["salesParties"],
    queryFn: async () => (await rmcClient.listSalesParties()).data,
  });

  const siteOptions = React.useMemo(
    () => buildSalesSiteOptions(parties, filters.party_id),
    [parties, filters.party_id]
  );

  const createSiteOptions = React.useMemo(
    () => buildSalesSiteOptions(parties, createPartyId),
    [parties, createPartyId]
  );

  const createSiteFilterParams = React.useMemo(
    () => buildSiteExportParams(createSiteId, createSiteOptions),
    [createSiteId, createSiteOptions]
  );

  React.useEffect(() => {
    if (!createOpen || !createSiteId) return;
    const ok = createSiteOptions.some((o) => o.id !== "" && String(o.id) === String(createSiteId));
    if (!ok) setCreateSiteId("");
  }, [createOpen, createSiteOptions, createSiteId]);

  const siteNameForApi = React.useMemo(() => {
    if (!filters.site_id) return "";
    const o = siteOptions.find((x) => String(x.id) === String(filters.site_id));
    return (o?.siteName || "").trim();
  }, [filters.site_id, siteOptions]);

  React.useEffect(() => {
    if (!filters.site_id) return;
    const ok = siteOptions.some((o) => o.id !== "" && String(o.id) === String(filters.site_id));
    if (!ok) setFilters((prev) => ({ ...prev, site_id: "" }));
  }, [siteOptions, filters.site_id]);

  const queryParams = React.useMemo(
    () => ({
      ...(filters.date_from ? { date_from: filters.date_from } : {}),
      ...(filters.date_to ? { date_to: filters.date_to } : {}),
      ...(filters.party_id ? { party_id: filters.party_id } : {}),
      ...(filters.tax_type ? { tax_type: filters.tax_type } : {}),
      ...(siteNameForApi ? { site_name: siteNameForApi } : {}),
      page,
      page_size: PAGE_SIZE,
    }),
    [filters.date_from, filters.date_to, filters.party_id, filters.tax_type, siteNameForApi, page]
  );
  const { data: invoicesData, isFetching } = useQuery({
    queryKey: ["salesInvoices", queryParams],
    queryFn: async () => (await rmcClient.listSalesInvoices(queryParams)).data,
    keepPreviousData: true,
  });
  const rows = React.useMemo(() => {
    if (Array.isArray(invoicesData)) return invoicesData;
    if (Array.isArray(invoicesData?.results)) return invoicesData.results;
    if (Array.isArray(invoicesData?.data?.results)) return invoicesData.data.results;
    return [];
  }, [invoicesData]);
  const paginationMeta = React.useMemo(() => readPaginatedListMeta(invoicesData), [invoicesData]);

  const totalCount = React.useMemo(() => {
    if (paginationMeta.count != null) return paginationMeta.count;
    if (paginationMeta.hasNext) {
      return (page - 1) * PAGE_SIZE + rows.length + 1;
    }
    return (page - 1) * PAGE_SIZE + rows.length;
  }, [paginationMeta, page, rows.length]);

  const totalPages = React.useMemo(() => {
    if (paginationMeta.totalPages != null) {
      return Math.max(1, paginationMeta.totalPages);
    }
    if (paginationMeta.count != null) {
      return Math.max(1, Math.ceil(paginationMeta.count / PAGE_SIZE));
    }
    if (paginationMeta.hasNext) {
      return Math.max(page + 1, 1);
    }
    return Math.max(1, page);
  }, [paginationMeta, page]);

  React.useEffect(() => {
    setPage((p) => (p > totalPages ? totalPages : p));
  }, [totalPages]);

  const currentPage = Math.min(page, totalPages);
  const activeFilterCount = React.useMemo(
    () =>
      [
        filters.date_from,
        filters.date_to,
        filters.party_id,
        filters.tax_type,
        filters.site_id,
      ].filter(Boolean).length,
    [filters]
  );
  const { data: detail, isLoading: isDetailLoading } = useQuery({
    queryKey: ["salesInvoice", detailId],
    queryFn: async () => (await rmcClient.getSalesInvoice(detailId)).data,
    enabled: detailId != null,
  });
  const detailChallanRef = React.useMemo(() => invoiceChallanDisplay(detail), [detail]);
  const { data: challans = [], isFetching: isFetchingChallans } = useQuery({
    queryKey: ["salesChallans", "invoiceCreate", createPartyId || "all", createSiteId || ""],
    queryFn: async () => {
      const payload = (
        await rmcClient.listSalesChallans({
          ...(createPartyId ? { party_id: createPartyId } : {}),
          ...createSiteFilterParams,
          pagination: false,
        })
      ).data;
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.results)) return payload.results;
      if (Array.isArray(payload?.data?.results)) return payload.data.results;
      return [];
    },
    enabled: createOpen,
  });

  const invoicedChallanIdSet = React.useMemo(() => {
    const set = new Set();
    rows.forEach((inv) => {
      const id = Number(inv?.challan_id ?? inv?.challan?.id);
      if (Number.isFinite(id) && id > 0) set.add(id);
      (inv?.challans || []).forEach((ch) => {
        const cid = Number(ch?.id);
        if (Number.isFinite(cid) && cid > 0) set.add(cid);
      });
    });
    return set;
  }, [rows]);
  const selectableChallans = React.useMemo(
    () =>
      (challans || []).map((c) => {
        const challanId = Number(c.id);
        return {
          ...c,
          challanId,
          alreadyInvoiced:
            challanIsInvoicedFromPayload(c) ||
            (Number.isFinite(challanId) && challanId > 0 && invoicedChallanIdSet.has(challanId)),
        };
      }),
    [challans, invoicedChallanIdSet]
  );

  const createDrawerChallans = React.useMemo(() => {
    if (!createSiteId) return selectableChallans;
    const n = Number(createSiteId);
    if (Number.isFinite(n) && n > 0 && String(n) === String(createSiteId).trim()) {
      return selectableChallans.filter(
        (c) => String(c.site_id ?? c.site?.id ?? "") === String(n),
      );
    }
    const opt = createSiteOptions.find((x) => String(x.id) === String(createSiteId));
    const siteName = opt?.siteName?.trim();
    if (!siteName) return selectableChallans;
    return selectableChallans.filter(
      (c) => challanSiteLabel(c, parties) === siteName,
    );
  }, [selectableChallans, createSiteId, createSiteOptions, parties]);

  const toggleChallan = (challanId, checked) => {
    setSelectedChallanIds((prev) => {
      if (checked) return Array.from(new Set([...prev, challanId]));
      return prev.filter((id) => id !== challanId);
    });
  };
  const selectAllEligible = () => {
    const all = createDrawerChallans
      .filter((c) => !c.alreadyInvoiced)
      .map((c) => c.challanId);
    setSelectedChallanIds(all);
  };
  const clearSelected = () => setSelectedChallanIds([]);

  const deleteInvoice = useMutation({
    mutationFn: (id) => rmcClient.deleteSalesInvoice(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["salesInvoices"] });
      qc.invalidateQueries({ queryKey: ["salesChallans"] });
      qc.removeQueries({ queryKey: ["salesInvoice", id] });
      setInvoiceDeleteId(null);
      setDetailId((prev) => (prev === id ? null : prev));
      toast.success("Invoice deleted");
    },
    onError: (e) => toast.error(handleApiError(e)),
  });

  const createInvoice = useMutation({
    mutationFn: async () => {
      const eligible = selectedChallanIds.filter((id) => {
        const row = selectableChallans.find((x) => x.challanId === Number(id));
        return row && !row.alreadyInvoiced;
      });
      if (!eligible.length) throw new Error("noEligible");
      const res = await rmcClient.createSalesInvoicesBulk({ challan_ids: eligible, tax_type: taxType });
      const failMsg = extractApiEnvelopeMessage(res?.data);
      if (failMsg) throw new Error(failMsg);
      return res;
    },
    onSuccess: () => {
      toast.success("Invoice(s) created");
      setCreateOpen(false);
      setSelectedChallanIds([]);
      setCreatePartyId(null);
      setCreateSiteId("");
      qc.invalidateQueries({ queryKey: ["salesInvoices"] });
      qc.invalidateQueries({ queryKey: ["salesChallans"] });
    },
    onError: (e) => {
      if (e.message === "noEligible") {
        toast.error("Select at least one non-invoiced challan.");
      } else {
        toast.error(handleApiError(e));
      }
    },
  });

  const partyName = React.useCallback(
    (id) => parties.find((p) => p.id === id)?.name || "—",
    [parties]
  );
  const taxTypeOptions = React.useMemo(
    () => [
      { id: "", name: "All" },
      { id: "GST", name: "GST" },
      { id: "NON_GST", name: "Non-GST" },
    ],
    []
  );

  const invoicePendingDelete = React.useMemo(() => {
    return rows.find((r) => r.id === invoiceDeleteId) || null;
  }, [rows, invoiceDeleteId]);

  const updateFilters = React.useCallback((next) => {
    setFilters((prev) => {
      const merged = { ...prev, ...next };
      if ("party_id" in next && next.party_id !== prev.party_id) {
        merged.site_id = "";
      }
      return merged;
    });
    setPage(1);
  }, []);

  const clearFilters = React.useCallback(() => {
    setFilters({
      date_from: isoToday,
      date_to: isoToday,
      party_id: "",
      tax_type: "",
      site_id: "",
    });
    setPage(1);
  }, [isoToday]);

  React.useEffect(() => {
    if (!rowPrintInvoice) return;
    let cancelled = false;
    const triggerPrint = async () => {
      await new Promise((resolve) => setTimeout(resolve, 0));
      if (cancelled) return;
      await rowPrintRef.current?.openPrint?.();
      if (!cancelled) setRowPrintInvoice(null);
    };
    triggerPrint();
    return () => {
      cancelled = true;
    };
  }, [rowPrintInvoice]);

  const exportFiltered = React.useCallback(async () => {
    setIsExporting(true);
    try {
      const payload = (
        await rmcClient.listSalesInvoices({
          ...(filters.date_from ? { date_from: filters.date_from } : {}),
          ...(filters.date_to ? { date_to: filters.date_to } : {}),
          ...(filters.party_id ? { party_id: filters.party_id } : {}),
          ...(filters.tax_type ? { tax_type: filters.tax_type } : {}),
          ...(siteNameForApi ? { site_name: siteNameForApi } : {}),
          pagination: false,
        })
      ).data;
      const exportRows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload?.data?.results)
            ? payload.data.results
            : [];
      const header = [
        "invoice_no",
        "date",
        "party",
        "challan_no",
        "tax_type",
        "gst_rate",
        "total_amount",
      ];
      const lines = exportRows.map((r) =>
        [
          r.invoice_no ?? `INV-${r.id ?? ""}`,
          r.date || r.invoice_date || "",
          partyName(r.party_id ?? r.party?.id),
          r.challan_no || r.challan?.challan_no || "",
          r.tax_type || "",
          r.gst_rate ?? "",
          r.total_amount ?? r.amount ?? "",
        ]
          .map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`)
          .join(",")
      );
      const csv = [header.join(","), ...lines].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sales-invoices-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${exportRows.length} invoices`);
    } catch {
      toast.error("Failed to export invoices.");
    } finally {
      setIsExporting(false);
    }
  }, [filters.date_from, filters.date_to, filters.party_id, filters.tax_type, siteNameForApi, partyName]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <PageBreadCrumb pageTitle="Sales Invoices" />
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={isExporting}
            onClick={exportFiltered}
          >
            {isExporting ? "Exporting…" : "Export All Filtered"}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setCreatePartyId(null);
              setCreateSiteId("");
              setSelectedChallanIds([]);
              setCreateOpen(true);
            }}
          >
            Create Invoice
          </Button>
        </div>
      </div>
      <div className="rounded-2xl border border-gray-200/80 bg-white/90 p-4 shadow-lg shadow-gray-200/50 ring-1 ring-gray-100/80 backdrop-blur-sm transition-all dark:border-gray-800 dark:bg-gray-900/50 dark:shadow-black/30 dark:ring-gray-800/80">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
              Smart Filters
            </p>
            {activeFilterCount > 0 && (
              <span className="menu-dropdown-badge-active rounded-full px-2.5 py-1 text-[11px] font-semibold">
                {activeFilterCount} active
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Showing {rows.length} of {totalCount} records
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Date From</label>
            <DatePicker
              id="sales-invoice-filter-from"
              defaultDate={filters.date_from}
              placeholder="From date"
              onChange={(_, dateStr) => updateFilters({ date_from: dateStr || "" })}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Date To</label>
            <DatePicker
              id="sales-invoice-filter-to"
              defaultDate={filters.date_to}
              placeholder="To date"
              onChange={(_, dateStr) => updateFilters({ date_to: dateStr || "" })}
            />
          </div>
          <SearchableSelect
            label="Party"
            value={filters.party_id}
            onChange={(v) => updateFilters({ party_id: v || "" })}
            options={[{ id: "", name: "All parties" }, ...parties]}
            getLabel={(o) => o.name}
            getValue={(o) => o.id}
            getSubLabel={(p) => p.gst_number}
            placeholder="All parties"
            overlayZClass="z-[100001]"
            menuZClass="z-[100002]"
          />
          <SearchableSelect
            label="Tax Type"
            value={filters.tax_type}
            onChange={(v) => updateFilters({ tax_type: v || "" })}
            options={taxTypeOptions}
            getLabel={(o) => o.name}
            getValue={(o) => o.id}
            placeholder="All"
            overlayZClass="z-[100001]"
            menuZClass="z-[100002]"
          />
          <SearchableSelect
            label="Site"
            value={filters.site_id}
            onChange={(v) => updateFilters({ site_id: v || "" })}
            options={siteOptions}
            getLabel={(o) => o.name}
            getValue={(o) => o.id}
            getSubLabel={(o) => (o.partyName && !filters.party_id ? o.partyName : undefined)}
            placeholder="All sites"
            overlayZClass="z-[100001]"
            menuZClass="z-[100002]"
          />
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-gray-200/70 pt-3 dark:border-gray-800">
          <Button type="button" size="sm" variant="outline" onClick={clearFilters}>
            Clear Filters
          </Button>
        </div>
      </div>
      <DataTable
        columns={[
          { key: "no", header: "Invoice No", render: (r) => r.invoice_no || `INV-${r.id}` },
          { key: "date", header: "Date", render: (r) => r.date || r.invoice_date || "—" },
          { key: "party", header: "Party", render: (r) => partyName(r.party_id ?? r.party?.id) },
          { key: "site", header: "Site", render: (r) => salesInvoiceSiteLabel(r) },
          { key: "challan", header: "Challan", render: (r) => r.challan_no || r.challan?.challan_no || "—" },
          {
            key: "tax",
            header: "Tax",
            render: (r) =>
              r.tax_type === "GST"
                ? `GST${r.gst_rate ? ` (${Number(r.gst_rate).toFixed(2)}%)` : ""}`
                : "Non-GST",
          },
          {
            key: "total",
            header: "Total",
            render: (r) => `₹${fmt(r.total_amount ?? r.amount ?? 0, 2)}`,
          },
          {
            key: "actions",
            header: "Actions",
            headerClassName: "text-right",
            cellClassName: "text-right",
            render: (r) => (
              <div className="inline-flex flex-wrap items-center justify-end gap-2">
                <Link
                  href={`/sales/invoices/${r.id}/edit`}
                  className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-3.5 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-white/5"
                  onClick={(e) => e.stopPropagation()}
                >
                  Edit
                </Link>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRowPrintInvoice(r);
                  }}
                >
                  Print
                </Button>
                <button
                  type="button"
                  aria-label={`Delete invoice ${r.invoice_no || r.id}`}
                  title="Delete"
                  disabled={deleteInvoice.isPending}
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10 disabled:opacity-50 dark:border-red-500/40"
                  onClick={(e) => {
                    e.stopPropagation();
                    setInvoiceDeleteId(r.id);
                  }}
                >
                  <TrashBinIcon className="size-4" />
                </button>
              </div>
            ),
          },
        ]}
        data={rows}
        isLoading={isFetching}
        onRowClick={(r) => setDetailId(r.id)}
      />
      <div className="flex justify-end">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(p) => setPage(Math.max(1, Math.min(totalPages, p)))}
        />
      </div>

      <Modal
        isOpen={detailId != null}
        onClose={() => setDetailId(null)}
        className="max-w-5xl w-full max-h-[min(90vh,820px)] overflow-y-auto"
      >
        {isDetailLoading ? (
          <div className="p-6 text-sm text-gray-400">Loading invoice…</div>
        ) : detail ? (
          <div className="space-y-5 px-1 pb-1 pt-3 sm:px-2">
            <div className="rounded-lg border border-gray-800 bg-gray-950/70 p-4">
              <div className="flex flex-wrap items-start justify-between gap-4 pr-12">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-gray-500">Invoice</p>
                  <p className="mt-1 text-xl font-semibold text-white">
                    {detail.invoice_no || `INV-${detail.id}`}
                  </p>
                </div>
                <div className="text-right text-xs text-gray-400">
                  <p>Date: {detail.date || detail.invoice_date || "—"}</p>
                  <p className="mt-1">
                    Tax:{" "}
                    <span className="text-gray-200">
                      {detail.tax_type === "GST"
                        ? `GST (${Number(detail.gst_rate || 0).toFixed(2)}%)`
                        : "Non-GST"}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-lg border border-gray-800 bg-gray-950/40 p-4">
                <p className="text-[11px] uppercase tracking-wider text-gray-500">Customer</p>
                <p className="mt-2 text-sm text-gray-200">
                  {detail.party?.name || partyName(detail.party_id ?? detail.party?.id)}
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  Site: {detail.site?.name || "—"}
                </p>
              </div>

              <div className="rounded-lg border border-gray-800 bg-gray-950/40 p-4">
                <p className="text-[11px] uppercase tracking-wider text-gray-500">Reference</p>
                <p className="mt-2 text-sm text-gray-200">Challan: {detailChallanRef.challanNo}</p>
                <p className="mt-1 text-xs text-gray-400">Batch No: {detailChallanRef.batchNo}</p>
                <p className="mt-1 text-xs text-gray-400">Challan ID: {detailChallanRef.challanId}</p>
              </div>
            </div>

            <div className="rounded-lg border border-gray-800 overflow-hidden">
              <div className="border-b border-gray-800 bg-gray-950/80 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                Invoice Items
              </div>
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-800 bg-gray-950/60">
                    <th className="px-4 py-3 font-semibold text-gray-500">Grade</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-500">Qty</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-500">Rate (₹)</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-500">Amount (₹)</th>
                    <th className="px-4 py-3 font-semibold text-gray-500">Remark</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {(detail.items || []).map((it, idx) => (
                    <tr key={idx} className="bg-gray-950/20">
                      <td className="px-4 py-3 text-gray-200">{it.grade?.name || `Grade #${idx + 1}`}</td>
                      <td className="px-4 py-3 text-right text-gray-200">{fmt(it.qty, 3)}</td>
                      <td className="px-4 py-3 text-right text-gray-200">{fmt(it.rate, 2)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-white">{fmt(it.amount, 2)}</td>
                      <td className="px-4 py-3 text-gray-400">{it.remark || "—"}</td>
                    </tr>
                  ))}
                  {(!detail.items || detail.items.length === 0) && (
                    <tr className="bg-gray-950/20">
                      <td className="px-4 py-4 text-gray-500" colSpan={5}>
                        No items available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  href={`/sales/invoices/${detail.id}/edit`}
                  className="inline-flex items-center justify-center rounded-lg border border-gray-600 px-3.5 py-2 text-xs font-medium text-gray-300 hover:bg-gray-800"
                >
                  Edit invoice
                </Link>
                <Button type="button" variant="outline" size="sm" onClick={() => printRef.current?.openPrint?.()}>
                  Print
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={deleteInvoice.isPending}
                  className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                  onClick={() => setInvoiceDeleteId(detail.id)}
                >
                  Delete
                </Button>
              </div>
              <div className="ml-auto w-full max-w-sm rounded-lg border border-gray-800 bg-gray-950/40 p-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-300">
                    <span>Sub Total</span>
                    <span className="font-medium text-white">₹ {fmt(detail.sub_total, 2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-300">
                    <span>GST ({detail.tax_type === "GST" ? `${Number(detail.gst_rate || 0).toFixed(2)}%` : "—"})</span>
                    <span className="font-medium text-white">
                      ₹ {detail.tax_type === "GST" ? fmt(detail.gst_amount, 2) : "0.00"}
                    </span>
                  </div>
                  <div className="mt-2 border-t border-gray-800 pt-2 flex justify-between text-base">
                    <span className="font-semibold text-gray-200">Grand Total</span>
                    <span className="font-semibold text-white">
                      ₹ {fmt(detail.total_amount ?? detail.amount ?? 0, 2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <InvoicePrint ref={printRef} invoice={detail} />
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={invoiceDeleteId != null}
        onClose={() => {
          if (!deleteInvoice.isPending) setInvoiceDeleteId(null);
        }}
        className="max-w-md w-full"
      >
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Delete sales invoice?</h3>
          <p className="text-sm leading-relaxed text-gray-400">
            {invoicePendingDelete?.invoice_no ? (
              <>
                This will permanently remove invoice{" "}
                <span className="font-medium text-gray-200">
                  {invoicePendingDelete.invoice_no || `INV-${invoicePendingDelete.id}`}
                </span>
                . Related challans may become available for invoicing again.
              </>
            ) : (
              <>This will permanently remove this invoice.</>
            )}
          </p>
          <div className="flex flex-wrap justify-end gap-2 border-t border-gray-800 pt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={deleteInvoice.isPending}
              onClick={() => setInvoiceDeleteId(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={deleteInvoice.isPending}
              className="bg-red-600 hover:bg-red-700"
              onClick={() => invoiceDeleteId != null && deleteInvoice.mutate(invoiceDeleteId)}
            >
              {deleteInvoice.isPending ? "Deleting…" : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>

      <InvoicePrint ref={rowPrintRef} invoice={rowPrintInvoice} />

      <DrawerPanel
        open={createOpen}
        onClose={() => {
          if (!createInvoice.isPending) {
            setCreateOpen(false);
            setSelectedChallanIds([]);
            setCreatePartyId(null);
            setCreateSiteId("");
          }
        }}
        title="Create Invoice"
        widthClass="max-w-3xl"
      >
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <SearchableSelect
              label="Party"
              value={createPartyId}
              onChange={(v) => {
                setCreatePartyId(v);
                setCreateSiteId("");
                setSelectedChallanIds([]);
              }}
              options={parties}
              getSubLabel={(p) => p.gst_number}
              placeholder="All parties"
              overlayZClass="z-[100001]"
              menuZClass="z-[100002]"
            />
            <SearchableSelect
              label="Site"
              value={createSiteId}
              onChange={(v) => {
                setCreateSiteId(v || "");
                setSelectedChallanIds([]);
              }}
              options={createSiteOptions}
              getLabel={(o) => o.name}
              getValue={(o) => o.id}
              getSubLabel={(o) => (o.partyName && !createPartyId ? o.partyName : undefined)}
              placeholder="All sites"
              overlayZClass="z-[100001]"
              menuZClass="z-[100002]"
            />
            <div className="sm:col-span-2">
              <p className="mb-2 text-xs text-gray-400">Tax Type</p>
              <div className="flex items-center gap-6">
                <Radio
                  id="create-invoice-gst"
                  name="create-invoice-tax"
                  value="GST"
                  label="GST"
                  checked={taxType === "GST"}
                  onChange={() => setTaxType("GST")}
                />
                <Radio
                  id="create-invoice-non-gst"
                  name="create-invoice-tax"
                  value="NON_GST"
                  label="Non-GST"
                  checked={taxType === "NON_GST"}
                  onChange={() => setTaxType("NON_GST")}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-400">Select Challans (multiple allowed)</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={selectAllEligible}>
                Select All Eligible
              </Button>
              <Button size="sm" variant="outline" onClick={clearSelected}>
                Clear
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-gray-800">
            <div className="max-h-[420px] overflow-auto divide-y divide-gray-800">
              {isFetchingChallans ? (
                <p className="p-3 text-xs text-gray-500">Loading challans…</p>
              ) : createDrawerChallans.length === 0 ? (
                <p className="p-3 text-xs text-gray-500">
                  {createSiteId
                    ? "No challans found for the selected party and site."
                    : "No challans found for selection."}
                </p>
              ) : (
                createDrawerChallans.map((c) => {
                  const checked = selectedChallanIds.includes(c.challanId);
                  const site = challanSiteLabel(c, parties);
                  const label = `${c.challan_no || `SAL-${c.challanId}`} • ${partyName(c.party_id)} • ${site} • ${c.date || "—"} • ₹${fmt(c.total_amount, 2)}`;
                  return (
                    <div key={c.challanId} className="flex items-center justify-between gap-3 px-3 py-2">
                      <Checkbox
                        id={`challan-${c.challanId}`}
                        checked={checked}
                        disabled={c.alreadyInvoiced}
                        onChange={(v) => toggleChallan(c.challanId, v)}
                        label={label}
                      />
                      {c.alreadyInvoiced && (
                        <span className="rounded bg-amber-500/10 px-2 py-1 text-[10px] text-amber-300">
                          Already invoiced
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-gray-800 pt-4">
            <Button
              size="sm"
              variant="outline"
              disabled={createInvoice.isPending}
              onClick={() => {
                setCreateOpen(false);
                setSelectedChallanIds([]);
                setCreatePartyId(null);
                setCreateSiteId("");
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={createInvoice.isPending || !selectedChallanIds.length}
              onClick={() => createInvoice.mutate()}
            >
              {createInvoice.isPending ? "Creating…" : "Create Invoice"}
            </Button>
          </div>
        </div>
      </DrawerPanel>
    </div>
  );
}
